/**
 * Shared JSON-LD for public /docs and /learn guide pages (MetaHead).
 *
 * Emits a rich `@graph` per page so search engines and LLM crawlers can
 * reliably identify:
 *   - the page (WebPage with breadcrumb, speakable, primary image)
 *   - the article body (TechArticle for /docs, LearningResource + Course for /learn)
 *   - the publisher + brand (Organization)
 *   - related FAQs/HowTo steps when provided
 *
 * Keeps schema consistent across dozens of static routes.
 */
import { getAllDocsPages } from '@/constants/docsNavigation';
import {
  getLearnGuidePages,
  LEARN_FAQS,
  LEARN_HUB,
  LEARN_MODULES,
  LEARN_OUTCOMES,
  LEARN_TOTAL_MINUTES,
} from '@/constants/learnCurriculum';
import { GUIDE_DATE_PUBLISHED, GUIDE_DATE_MODIFIED } from '@/constants/guideSeoDates';
import { resolveShareImage } from '@/lib/socialShareCards';
import { tutorialVideoNodes } from '@/lib/tutorialVideoSchema';

const SITE_URL = 'https://gab.ai';
const ORG_NAME = 'Gab AI Inc.';
const BRAND_NAME = 'Gab AI';
const LOGO_URL = 'https://cdn.gab.ai/static/media/gab-ai-logo.png';

/**
 * Flat list of learn URLs for hub JSON-LD ItemList.
 * Sourced from `frontend/constants/learnCurriculum.js`.
 * Keep `LEARN_PATHS` in `backend/constants/publicGuidesSitemapPaths.js` in sync.
 */
const LEARN_GUIDE_PAGES = getLearnGuidePages();

function absUrl(canonicalPath) {
  if (!canonicalPath || typeof canonicalPath !== 'string') return SITE_URL;
  const path = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return `${SITE_URL}${path}`;
}

function breadcrumbNameFromTitle(title, guideType) {
  if (!title) return 'Guide';
  if (guideType === 'learn') {
    return title.replace(/\s*[-–—|:]\s*Learn AI\s*$/i, '').trim() || title;
  }
  return title
    .replace(/^Documentation\s*[-–—|:]\s*/i, '')
    .replace(/\s*[-–—|:]\s*Documentation\s*$/i, '')
    .trim() || title;
}

function asArray(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(/\s*,\s*/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [value].filter(Boolean);
}

/**
 * Build an ISO-8601 duration like "PT6M" from a minutes count.
 * @param {number|string|undefined} minutes
 */
function isoDurationFromMinutes(minutes) {
  const n = typeof minutes === 'number' ? minutes : parseInt(minutes, 10);
  if (!n || Number.isNaN(n) || n <= 0) return undefined;
  return `PT${n}M`;
}

const ORG_NODE = {
  '@type': 'Organization',
  '@id': `${SITE_URL}#organization`,
  name: ORG_NAME,
  alternateName: BRAND_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: LOGO_URL,
    width: 512,
    height: 512,
  },
  sameAs: [
    'https://gab.com',
    'https://x.com/Gab__AI',
    'https://www.youtube.com/@GabdotAI',
  ],
};

const WEBSITE_NODE = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}#website`,
  url: SITE_URL,
  name: BRAND_NAME,
  publisher: { '@id': `${SITE_URL}#organization` },
  inLanguage: 'en-US',
};

/**
 * JSON-LD for a single docs or learn guide page.
 *
 * @param {object} opts
 * @param {string} opts.canonical                  — path, e.g. `/docs/memory`
 * @param {string} opts.title                      — meta title (matches MetaHead `title`)
 * @param {string} opts.description                — meta description
 * @param {'docs'|'learn'} opts.guideType
 * @param {string|string[]} [opts.keywords]        — keywords (comma string or array)
 * @param {string} [opts.image]                    — absolute hero/social image URL
 * @param {number} [opts.readMinutes]              — estimated read/lesson length in minutes
 * @param {'Beginner'|'Intermediate'|'Advanced'} [opts.difficulty]
 * @param {string} [opts.articleSection]           — TechArticle section (e.g. "API", "Models")
 * @param {string|string[]} [opts.about]           — Thing(s) this page is about
 * @param {string} [opts.audience]                 — Audience type (e.g. "Developers")
 * @param {string} [opts.datePublished]            — ISO date (defaults to corpus baseline)
 * @param {string} [opts.dateModified]             — ISO date (defaults to corpus baseline)
 * @param {string[]} [opts.teaches]                — learning outcomes (LearningResource)
 * @param {{question:string, answer:string}[]} [opts.faqs] — optional FAQPage entries
 * @param {{name:string, text:string, image?:string}[]} [opts.howToSteps] — optional HowTo steps
 * @param {string} [opts.howToName]                — HowTo title (defaults to page title)
 */
export function publicGuidePageStructuredData({
  canonical,
  title,
  description,
  guideType,
  keywords,
  image,
  readMinutes,
  difficulty,
  articleSection,
  about,
  audience,
  datePublished,
  dateModified,
  teaches,
  faqs,
  howToSteps,
  howToName,
}) {
  const url = absUrl(canonical);
  const pageName = breadcrumbNameFromTitle(title, guideType);
  const hubPath = guideType === 'learn' ? '/docs#learn' : '/docs';
  const hubName = guideType === 'learn' ? 'Learn' : 'Documentation';
  const hubUrl = absUrl(hubPath);
  const isHub = canonical === hubPath;

  const heroImage = resolveShareImage(canonical, image);
  const published = datePublished || GUIDE_DATE_PUBLISHED;
  const modified = dateModified || GUIDE_DATE_MODIFIED;
  const kwArray = asArray(keywords);
  const aboutArray = asArray(about);
  const audienceName = audience || (guideType === 'learn' ? 'AI users and prompt engineers' : 'Gab AI users and developers');

  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: hubName, item: hubUrl },
  ];
  if (!isHub) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: pageName,
      item: url,
    });
  }

  const imageNode = {
    '@type': 'ImageObject',
    '@id': `${url}#primaryimage`,
    url: heroImage,
    contentUrl: heroImage,
    width: 1200,
    height: 630,
    caption: title,
  };

  const webPage = {
    '@type': guideType === 'learn' ? ['WebPage', 'LearningResource'] : 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    headline: pageName,
    description,
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}#website` },
    breadcrumb: { '@id': `${url}#breadcrumb` },
    primaryImageOfPage: { '@id': `${url}#primaryimage` },
    datePublished: published,
    dateModified: modified,
    lastReviewed: modified,
    reviewedBy: { '@id': `${SITE_URL}#organization` },
    publisher: { '@id': `${SITE_URL}#organization` },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', 'h3', '[data-speakable="true"]'],
    },
    isAccessibleForFree: true,
    isFamilyFriendly: true,
    license: 'https://gab.ai/terms',
  };
  if (kwArray.length) webPage.keywords = kwArray;
  if (aboutArray.length) webPage.about = aboutArray.map((name) => ({ '@type': 'Thing', name }));

  const graph = [
    ORG_NODE,
    WEBSITE_NODE,
    imageNode,
    webPage,
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: breadcrumbItems,
    },
  ];

  const sharedAuthorPublisher = {
    author: { '@id': `${SITE_URL}#organization` },
    publisher: { '@id': `${SITE_URL}#organization` },
  };

  if (guideType === 'learn') {
    const learning = {
      '@type': ['LearningResource', 'Course'],
      '@id': `${url}#learningresource`,
      name: pageName,
      alternateName: title,
      description,
      url,
      image: { '@id': `${url}#primaryimage` },
      mainEntityOfPage: { '@id': `${url}#webpage` },
      inLanguage: 'en-US',
      isAccessibleForFree: true,
      isPartOf: { '@id': `${SITE_URL}#website` },
      educationalUse: 'self-study',
      learningResourceType: 'lesson',
      interactivityType: 'mixed',
      educationalLevel: difficulty || 'Beginner',
      proficiencyLevel: difficulty || 'Beginner',
      typicalAgeRange: '13-',
      audience: {
        '@type': 'EducationalAudience',
        educationalRole: 'general public',
        audienceType: audienceName,
      },
      provider: { '@id': `${SITE_URL}#organization` },
      // Required-for-Course richer fields:
      courseMode: 'online',
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: isoDurationFromMinutes(readMinutes) || 'PT5M',
        inLanguage: 'en-US',
        instructor: { '@id': `${SITE_URL}#organization` },
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: hubUrl,
      },
      ...sharedAuthorPublisher,
      datePublished: published,
      dateModified: modified,
    };
    if (kwArray.length) learning.keywords = kwArray;
    if (aboutArray.length) learning.about = aboutArray.map((name) => ({ '@type': 'Thing', name }));
    const teachesArr = asArray(teaches);
    if (teachesArr.length) {
      learning.teaches = teachesArr;
      learning.learningOutcome = teachesArr;
    }
    const duration = isoDurationFromMinutes(readMinutes);
    if (duration) learning.timeRequired = duration;
    graph.push(learning);
  } else {
    const tech = {
      '@type': 'TechArticle',
      '@id': `${url}#techarticle`,
      headline: pageName,
      name: title,
      description,
      url,
      image: { '@id': `${url}#primaryimage` },
      mainEntityOfPage: { '@id': `${url}#webpage` },
      inLanguage: 'en-US',
      isAccessibleForFree: true,
      isFamilyFriendly: true,
      datePublished: published,
      dateModified: modified,
      ...sharedAuthorPublisher,
      audience: {
        '@type': 'Audience',
        audienceType: audienceName,
      },
    };
    if (articleSection) tech.articleSection = articleSection;
    if (kwArray.length) tech.keywords = kwArray;
    if (aboutArray.length) tech.about = aboutArray.map((name) => ({ '@type': 'Thing', name }));
    const duration = isoDurationFromMinutes(readMinutes);
    if (duration) tech.timeRequired = duration;
    if (difficulty) tech.proficiencyLevel = difficulty;
    graph.push(tech);
  }

  if (Array.isArray(faqs) && faqs.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer,
        },
      })),
    });
  }

  if (Array.isArray(howToSteps) && howToSteps.length) {
    graph.push({
      '@type': 'HowTo',
      '@id': `${url}#howto`,
      name: howToName || pageName,
      description,
      totalTime: isoDurationFromMinutes(readMinutes),
      step: howToSteps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.name,
        text: s.text,
        ...(s.image ? { image: s.image } : {}),
      })),
    });
  }

  graph.push(...tutorialVideoNodes(canonical, url));

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/** `/docs` hub — CollectionPage + ItemList of doc topics + breadcrumbs. */
export function docsHubStructuredData() {
  const pages = getAllDocsPages();
  const hubUrl = absUrl('/docs');
  const itemListElement = pages.slice(0, 80).map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: p.label,
    description: p.description,
    url: absUrl(p.href),
  }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORG_NODE,
      WEBSITE_NODE,
      {
        '@type': 'CollectionPage',
        '@id': `${hubUrl}#collection`,
        url: hubUrl,
        name: 'Gab AI Documentation',
        description:
          'Official Gab AI documentation: chat, voice, images, video, music, tools, API, credits, and privacy. Complete reference for the all-in-one AI platform.',
        isPartOf: { '@id': `${SITE_URL}#website` },
        publisher: { '@id': `${SITE_URL}#organization` },
        inLanguage: 'en-US',
        datePublished: GUIDE_DATE_PUBLISHED,
        dateModified: GUIDE_DATE_MODIFIED,
        isAccessibleForFree: true,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2'],
        },
        breadcrumb: { '@id': `${hubUrl}#breadcrumb` },
        mainEntity: {
          '@type': 'ItemList',
          name: 'Gab AI documentation topics',
          numberOfItems: pages.length,
          itemListElement,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${hubUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Documentation', item: hubUrl },
        ],
      },
      ...tutorialVideoNodes('/docs', hubUrl),
    ],
  };
}

/** `/docs#learn` hub — CollectionPage + Course + ItemList + FAQ + HowTo. */
export function learnHubStructuredData() {
  const hubUrl = absUrl('/docs');
  const itemListElement = LEARN_GUIDE_PAGES.filter((p) => p.path !== '/docs#learn').map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: p.label,
    url: absUrl(p.path),
  }));

  const hasPart = LEARN_MODULES.map((mod) => ({
    '@type': 'LearningResource',
    name: `${mod.navTitle}: ${mod.title}`,
    description: mod.description,
    educationalLevel: Number(mod.number) <= 2 ? 'Beginner' : 'Intermediate',
    timeRequired: `PT${mod.minutes}M`,
    url: absUrl(`/docs/learn/${mod.lessons[0].slug}`),
    learningResourceType: 'module',
    teaches: mod.lessons.map((lesson) => lesson.label),
  }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORG_NODE,
      WEBSITE_NODE,
      {
        '@type': 'CollectionPage',
        '@id': `${hubUrl}#collection`,
        url: hubUrl,
        name: LEARN_HUB.seoTitle,
        headline: LEARN_HUB.headline,
        description: LEARN_HUB.seoDescription,
        about: [
          { '@type': 'Thing', name: 'How to use AI' },
          { '@type': 'Thing', name: 'Prompt engineering' },
          { '@type': 'Thing', name: 'AI image generation' },
        ],
        isPartOf: { '@id': `${SITE_URL}#website` },
        publisher: { '@id': `${SITE_URL}#organization` },
        inLanguage: 'en-US',
        datePublished: GUIDE_DATE_PUBLISHED,
        dateModified: GUIDE_DATE_MODIFIED,
        isAccessibleForFree: true,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2'],
        },
        breadcrumb: { '@id': `${hubUrl}#breadcrumb` },
        mainEntity: { '@id': `${hubUrl}#course` },
      },
      {
        '@type': ['Course', 'LearningResource'],
        '@id': `${hubUrl}#course`,
        name: LEARN_HUB.seoTitle,
        alternateName: ['Learn AI', 'Learn how to use AI', 'Free AI course for beginners'],
        description: LEARN_HUB.seoDescription,
        url: hubUrl,
        provider: { '@id': `${SITE_URL}#organization` },
        publisher: { '@id': `${SITE_URL}#organization` },
        inLanguage: 'en-US',
        isAccessibleForFree: true,
        educationalLevel: 'Beginner',
        educationalUse: 'self-study',
        learningResourceType: 'course',
        interactivityType: 'mixed',
        typicalAgeRange: '13-',
        courseMode: 'online',
        teaches: LEARN_OUTCOMES.map((item) => item.title),
        about: [
          { '@type': 'Thing', name: 'How to use AI' },
          { '@type': 'Thing', name: 'Prompt engineering' },
          { '@type': 'Thing', name: 'AI image generation' },
          { '@type': 'Thing', name: 'Custom AI agents' },
          { '@type': 'Thing', name: 'Agentic workflows' },
        ],
        hasPart,
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          courseWorkload: `PT${LEARN_TOTAL_MINUTES}M`,
          inLanguage: 'en-US',
          instructor: { '@id': `${SITE_URL}#organization` },
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          category: 'Free',
          url: hubUrl,
        },
      },
      {
        '@type': 'ItemList',
        '@id': `${hubUrl}#lessons`,
        name: 'Free AI lessons',
        numberOfItems: itemListElement.length,
        itemListElement,
      },
      {
        '@type': 'HowTo',
        '@id': `${hubUrl}#howto`,
        name: 'How to start learning AI',
        description: 'Read a short lesson, watch its video tutorial, then try the practice prompt in Gab AI.',
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: 'Read a short lesson',
            text: 'Pick a free lesson, from what AI is to writing better prompts, and read the skill in a few minutes.',
            url: absUrl('/docs/learn/what-is-ai'),
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: 'Watch the video tutorial',
            text: 'Each lesson has a short narrated video that shows the skill step by step in Gab AI.',
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: 'Try it yourself',
            text: 'Copy the practice prompt into Gab AI, read the answer, and ask for one specific change.',
            url: `${SITE_URL}/signup`,
          },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${hubUrl}#faq`,
        mainEntity: LEARN_FAQS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${hubUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Learn how to use AI', item: hubUrl },
        ],
      },
      ...tutorialVideoNodes('/docs#learn', hubUrl),
    ],
  };
}


export function guidesHubStructuredData() {
  const docs = docsHubStructuredData();
  const learn = learnHubStructuredData();
  const ids = new Set(docs['@graph'].map(node => node['@id']));
  return { ...docs, '@graph': [...docs['@graph'], ...learn['@graph'].filter(node => !ids.has(node['@id']))] };
}
